import React, { createContext, useContext, useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import SocialMedia from './pages/SocialMedia';
import ContentStudio from './pages/ContentStudio';
import Settings from './pages/Settings';
import Campaigns from './pages/Campaigns';
import Analytics from './pages/Analytics';
import BrandManagement from './pages/BrandManagement';
import AdManager from './pages/AdManager';
import LeadsCRM from './pages/LeadsCRM';
import Automation from './pages/Automation';
import AIAssistant from './pages/AIAssistant';
import Layout from './components/Layout';
import './index.css';

const API = process.env.REACT_APP_BACKEND_URL;

// Helper function to convert hex to HSL
function hexToHSL(hex) {
  // Remove # if present
  hex = hex.replace(/^#/, '');
  
  // Parse hex values
  let r = parseInt(hex.substring(0, 2), 16) / 255;
  let g = parseInt(hex.substring(2, 4), 16) / 255;
  let b = parseInt(hex.substring(4, 6), 16) / 255;

  let max = Math.max(r, g, b);
  let min = Math.min(r, g, b);
  let h, s, l = (max + min) / 2;

  if (max === min) {
    h = s = 0;
  } else {
    let d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
      case g: h = ((b - r) / d + 2) / 6; break;
      case b: h = ((r - g) / d + 4) / 6; break;
      default: h = 0;
    }
  }

  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

// Brand Context - Only Primary Color
const BrandContext = createContext(null);

export const useBrand = () => useContext(BrandContext);

const DEFAULT_PRIMARY = '#E36559';

function BrandProvider({ children }) {
  const [primaryColor, setPrimaryColor] = useState(DEFAULT_PRIMARY);

  useEffect(() => {
    // Apply primary color as CSS variable
    const root = document.documentElement;
    root.style.setProperty('--primary', hexToHSL(primaryColor));
    root.style.setProperty('--ring', hexToHSL(primaryColor));
  }, [primaryColor]);

  const updatePrimaryColor = (value) => {
    setPrimaryColor(value);
  };

  const resetPrimaryColor = () => {
    setPrimaryColor(DEFAULT_PRIMARY);
  };

  return (
    <BrandContext.Provider value={{ primaryColor, updatePrimaryColor, resetPrimaryColor, DEFAULT_PRIMARY }}>
      {children}
    </BrandContext.Provider>
  );
}

// Theme Context
const ThemeContext = createContext(null);

export const useTheme = () => useContext(ThemeContext);

function ThemeProvider({ children }) {
  const [theme, setTheme] = useState('light');

  useEffect(() => {
    // Apply theme to root element
    if (theme === 'light') {
      document.documentElement.classList.add('light');
    } else {
      document.documentElement.classList.remove('light');
    }
  }, [theme]);

  const toggleTheme = () => {
    setTheme(prev => prev === 'dark' ? 'light' : 'dark');
  };

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

// Auth Context
const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('marketing_token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const response = await fetch(`${API}/api/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setUser(data);
      } else {
        logout();
      }
    } catch (error) {
      console.error('Failed to fetch user:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = (newToken, userData) => {
    localStorage.setItem('marketing_token', newToken);
    setToken(newToken);
    setUser(userData);
  };

  const logout = () => {
    localStorage.removeItem('marketing_token');
    setToken(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, token, login, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
}

function PrivateRoute({ children }) {
  const { token, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }
  
  return token ? children : <Navigate to="/login" />;
}

function ThemedToaster() {
  const { theme } = useTheme();
  
  return (
    <Toaster 
      position="top-right" 
      toastOptions={{
        style: theme === 'dark' ? {
          background: 'hsl(30 12% 12%)',
          border: '1px solid hsl(30 10% 20%)',
          color: 'hsl(40 20% 90%)',
        } : {
          background: 'hsl(0 0% 100%)',
          border: '1px solid hsl(40 15% 85%)',
          color: 'hsl(30 15% 15%)',
        },
      }}
    />
  );
}

function App() {
  return (
    <ThemeProvider>
      <BrandProvider>
        <AuthProvider>
          <BrowserRouter>
            <ThemedToaster />
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/" element={<PrivateRoute><Layout /></PrivateRoute>}>
                <Route index element={<Navigate to="/dashboard" replace />} />
                <Route path="dashboard" element={<Dashboard />} />
                <Route path="campaigns" element={<Campaigns />} />
                <Route path="social-media" element={<SocialMedia />} />
              <Route path="content-studio" element={<ContentStudio />} />
              <Route path="ad-manager" element={<AdManager />} />
              <Route path="leads-crm" element={<LeadsCRM />} />
              <Route path="automation" element={<Automation />} />
              <Route path="analytics" element={<Analytics />} />
              <Route path="brand-management" element={<BrandManagement />} />
              <Route path="ai-assistant" element={<AIAssistant />} />
              <Route path="settings" element={<Settings />} />
            </Route>
          </Routes>
        </BrowserRouter>
      </AuthProvider>
      </BrandProvider>
    </ThemeProvider>
  );
}

export default App;
